/**
 * Documents on a project page, and the drafts kept on this computer (Home).
 *
 * Executives: "Start a document" per template and their own documents.
 * Project owners and admins: every document's state, who started it and when;
 * never the values (the server does not give them).
 * Names shown next to an executive's own documents come from THIS computer
 * only (IndexedDB); the server list has no names in it.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { formatRegistration } from '@saledeed/shared';
import { api } from '../api/client';
import type { DocumentSummary, ProjectDetail } from '../api/types';
import { useCan, useUser } from '../auth/session';
import { Badge, Button, ErrorBox, formatDateTime, Notice, TableCard } from '../components/ui';
import { draftStore, withTimeout, type LocalDraft } from './local-store';

/** sessionStorage key for the one-shot "Registered as …" notice shown on the project page. */
export const FLASH_KEY = 'saledeed.flash';

function useDeviceDrafts() {
  const { user } = useUser();
  return useQuery({
    queryKey: ['device-drafts', user.id],
    queryFn: () => withTimeout(draftStore().list(user.id)).catch(() => [] as LocalDraft[]),
    staleTime: 0,
  });
}

/** Owner 10-21: finished documents wait for their registration number; then they are gone for the executive. */
const StateBadge = ({ doc, own }: { doc: DocumentSummary; own: boolean }) =>
  doc.registration ? <Badge tone="green">Registered: {formatRegistration(doc.registration)}</Badge>
    : doc.state === 'completed' ? (
      <span className="inline-flex flex-col items-start gap-0.5">
        <Badge tone="green">Finished</Badge>
        <span className="text-xs text-slate-600">{own ? 'enter the registration number' : 'waiting for the registration number'}</span>
      </span>
    ) : <Badge tone="amber">Draft</Badge>;

/** "Start a document" buttons, one per template (executives with document.fill). */
export function StartDocument({ projectId, templateCode, title }: { projectId: string; templateCode: string; title: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const start = useMutation({
    mutationFn: () => api<{ document: DocumentSummary }>('POST', `/api/projects/${projectId}/documents`, { body: { templateCode } }),
    onSuccess: r => {
      void qc.invalidateQueries({ queryKey: ['project-documents', projectId] });
      void navigate({ to: '/projects/$id/documents/$docId', params: { id: projectId, docId: r.document.id } });
    },
  });
  return (
    <span className="inline-flex flex-col items-end">
      <Button tone="primary" disabled={start.isPending} onClick={() => start.mutate()} aria-label={`Start a document from ${title}`}>
        {start.isPending ? 'Starting…' : 'Start a document'}
      </Button>
      {start.error && <ErrorBox error={start.error} />}
    </span>
  );
}

export function ProjectDocuments({ data }: { data: ProjectDetail }) {
  const { project } = data;
  const canFill = useCan('document.fill');
  const list = useQuery({
    queryKey: ['project-documents', project.id],
    queryFn: () => api<{ documents: DocumentSummary[] }>('GET', `/api/projects/${project.id}/documents`),
    enabled: project.kind === 'work',
  });
  const device = useDeviceDrafts();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(() => { try { return sessionStorage.getItem(FLASH_KEY); } catch { return null; } });
  useEffect(() => { try { sessionStorage.removeItem(FLASH_KEY); } catch { /* private mode */ } }, []);
  const remove = useMutation({
    mutationFn: async (d: DocumentSummary) => {
      await api('DELETE', `/api/documents/${d.id}`);
      const local = device.data?.find(x => x.documentId === d.id);
      if (local) await draftStore().delete(local.key).catch(() => {});
    },
    onSuccess: () => {
      setConfirm(null);
      void qc.invalidateQueries({ queryKey: ['project-documents', project.id] });
      void qc.invalidateQueries({ queryKey: ['device-drafts'] });
    },
  });
  if (project.kind !== 'work') return null;
  const docs = list.data?.documents ?? [];
  const onDevice = new Map((device.data ?? []).map(d => [d.documentId, d]));

  return (
    <section aria-labelledby="docs-heading" className="flex flex-col gap-2">
      <h2 id="docs-heading" className="text-lg font-semibold">{canFill ? 'My documents' : 'Documents'} ({docs.length})</h2>
      {flash && <Notice tone="success" onDismiss={() => setFlash(null)}>{flash}</Notice>}
      {!canFill && <p className="text-xs text-slate-500">Documents your executives started here. What they typed is private to them and is never shown here.</p>}
      <ErrorBox error={list.error} />
      {docs.length ? (
        <TableCard>
          <caption className="sr-only">Documents in this project</caption>
          <thead>
            <tr className="border-b border-slate-300 text-left">
              <th scope="col" className="py-2">Template</th>
              {canFill ? <th scope="col">Parties (this computer)</th> : <th scope="col">Started by</th>}
              <th scope="col">State</th><th scope="col">Started</th><th scope="col">Last change</th>
              {canFill && <th scope="col"><span className="sr-only">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {docs.map(d => {
              const local = onDevice.get(d.id);
              return (
                <tr key={d.id} className="border-b border-slate-200" data-document={d.id}>
                  <td className="py-2">
                    {canFill
                      ? <Link to="/projects/$id/documents/$docId" params={{ id: project.id, docId: d.id }} className="font-medium text-blue-800 underline">{d.templateTitle}</Link>
                      : <span className="font-medium">{d.templateTitle}</span>}
                    <span className="block text-xs text-slate-500">v{d.templateVersion} · {d.id.slice(0, 8)}</span>
                  </td>
                  {canFill
                    ? <td className="text-xs">{local ? (local.names || 'saved on this computer') : <span className="text-slate-400">not on this computer</span>}</td>
                    : <td>{d.createdBy.displayName} <code className="text-xs">{d.createdBy.userCode}</code></td>}
                  <td><StateBadge doc={d} own={canFill} /></td>
                  <td className="text-xs text-slate-600">{formatDateTime(d.createdAt)}</td>
                  <td className="text-xs text-slate-600">{formatDateTime(d.completedAt ?? d.updatedAt)}</td>
                  {canFill && (
                    <td className="whitespace-nowrap text-right">
                      <Link to="/projects/$id/documents/$docId" params={{ id: project.id, docId: d.id }} className="px-2 text-sm text-blue-800 underline">
                        {d.state === 'draft' ? 'Continue' : 'Open'}
                      </Link>
                      {d.state === 'draft' && (confirm === d.id ? (
                        <>
                          <Button tone="danger" disabled={remove.isPending} onClick={() => remove.mutate(d)}>Delete it</Button>
                          <Button tone="ghost" onClick={() => setConfirm(null)}>Keep</Button>
                        </>
                      ) : <Button tone="ghost" onClick={() => setConfirm(d.id)} aria-label={`Delete draft ${d.id.slice(0, 8)}`}>Delete</Button>)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </TableCard>
      ) : (
        <p className="text-sm text-slate-500">{list.isPending ? 'Loading…' : canFill ? 'No documents yet. Press "Start a document" next to a template above.' : 'No documents yet.'}</p>
      )}
      <ErrorBox error={remove.error} />
    </section>
  );
}

/** Home: drafts and finished documents kept on this computer, for this person only. */
export function DeviceDrafts() {
  const device = useDeviceDrafts();
  const list = device.data ?? [];
  if (!list.length) return null;
  return (
    <section aria-labelledby="device-heading" className="flex flex-col gap-2">
      <h2 id="device-heading" className="text-lg font-semibold">On this computer ({list.length})</h2>
      <p className="text-xs text-slate-500">Documents you worked on in this browser. Open one to carry on where you stopped.</p>
      <ul className="flex flex-col divide-y divide-slate-200 rounded border border-slate-200">
        {list.map(d => (
          <li key={d.key} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
            <Link to="/projects/$id/documents/$docId" params={{ id: d.projectId, docId: d.documentId }} className="font-medium text-blue-800 underline">{d.templateTitle}</Link>
            <span className="text-slate-600">{d.names}</span>
            <span className="text-xs text-slate-500">· {d.projectName} · {formatDateTime(d.updatedAt)}</span>
            {d.state === 'completed' ? <Badge tone="green">Finished · waiting for the registration number</Badge> : <Badge tone="amber">Draft</Badge>}
            {d.dirty && <Badge tone="red">not yet on the server</Badge>}
          </li>
        ))}
      </ul>
    </section>
  );
}
